import oddsApi from 'api/oddsApi';
import walletApi from 'api/walletApi';
import { useAppDispatch, useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import { RootState } from 'app/store';
import BigNumber from 'bignumber.js';
import ConditionButton from 'components/Commons/ConditionButton';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { MARKET_SPREADS, MARKET_TOTALS, MONEY_LINE_MARKET, PLACE_BET_AUDIO } from 'consts/app';
import Typo from 'consts/typography';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { useHandleInsufficientBalance } from 'hooks/useHandleInsufficientBalance';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import { OddModel } from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { clearOddsStatusById, selectOddStatus } from 'redux/gameSlice';
import { platformStates } from 'redux/platformSlice';
import { selectTeasers } from 'redux/teaserSlice';
import { convertOddsAmericanToDecimal, lowerPrice } from 'utils/betUtils';
import { isTablet } from 'utils/commonUtils';
import {
  selectBets,
  selectCurrentConverter,
  selectCurrentTurbo,
  setFetchMyBet,
} from '../homeSlice';
import { handleOddClick, handleOddTeaserClick } from '../logic/common/odd';

interface BetButtonProps {
  odd: OddModel;
  isShowAnimation?: string;
  index: number;
  gameId: string;
  homeTeam: string;
  awayTeam: string;
  category: string;
  homeTeamLogo: string;
  awayTeamLogo: string;
  gameStatus: string;
}

const aud = new Audio(PLACE_BET_AUDIO);

const BetButton: React.FunctionComponent<BetButtonProps> = ({
  odd,
  isShowAnimation = 'none',
  index,
  gameId,
  homeTeam,
  awayTeam,
  category,
  homeTeamLogo,
  awayTeamLogo,
  gameStatus,
}) => {
  const {
    id,
    price: bottomValue,
    decimalPrice: bottomDecimalValue,
    market,
    betPoints: topValue,
  } = odd;
  const [showAnimation, setShowAnimation] = useState<{ [value: string]: string }>({
    border: '',
    text: '',
  });
  const dispatch = useAppDispatch();
  const bets = useAppSelector(selectBets);
  const teasers = useAppSelector(selectTeasers);
  const platform = useAppSelector(platformStates);
  const converter = useAppSelector(selectCurrentConverter);
  const currentTurbo = useAppSelector(selectCurrentTurbo);
  const oddStatus = useAppSelector((state: RootState) => selectOddStatus(state, odd.id));
  const t = useAppTranslation();
  const et = useErrTranslation();

  const isActive = useMemo(() => bets.find((bet) => bet.odd.id === odd.id), [bets, odd.id]);

  const handleResponseError = useHandleResponseError();
  const handleInsufficientBalance = useHandleInsufficientBalance();
  const { getWallet } = useHandleGetWallets();
  const { currentUser } = useUserDetail();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (oddStatus !== 'none') {
      if (oddStatus === 'increase')
        setShowAnimation({ text: 'animated-increase', border: 'animated-border-green' });
      if (oddStatus === 'decrease')
        setShowAnimation({ text: 'animated-decrease', border: 'animated-border-red' });
      setTimeout(() => {
        setShowAnimation({ border: '', text: '' });
        dispatch(clearOddsStatusById(odd.id));
      }, 2000);
    } else {
      setShowAnimation({ border: '', text: '' });
    }
  }, [oddStatus]);

  const onClickBet = () => {
    handleOddClick(
      odd,
      bets,
      dispatch,
      homeTeam,
      awayTeam,
      gameId,
      category,
      gameStatus,
      homeTeamLogo,
      awayTeamLogo,
    );
    handleOddTeaserClick(
      odd,
      teasers,
      dispatch,
      homeTeam,
      awayTeam,
      gameId,
      category,
      homeTeamLogo,
      awayTeamLogo,
    );
  };

  const getPoint = () => {
    let prefix = '';
    if (MARKET_TOTALS.includes(market)) {
      prefix = index === 1 ? 'U ' : 'O ';
    }
    if (MARKET_SPREADS.includes(market)) {
      prefix = new BigNumber(topValue || 0).comparedTo(new BigNumber(0)) > 0 ? '+' : '';
    }
    return prefix + new BigNumber(topValue || 0).toFixed(1);
  };

  const onPlaceBet = async () => {
    if (!currentTurbo || currentTurbo === 'none') return;
    setIsLoading(true);
    let bettingAmount = Number(currentTurbo);
    if (currentUser?.coinDto.symbol !== 'USD') {
      const { ok, body } = await walletApi.getTokenPrice(currentUser?.coinDto.symbol as string);
      if (ok && body) {
        bettingAmount = bettingAmount / body.price;
      }
    }
    const { ok, error } = await oddsApi.placingBets({
      bets: [
        {
          gameId,
          oddsId: id,
          amount: bettingAmount,
          market,
        },
      ],
    });
    setIsLoading(false);
    if (ok) {
      aud.play();
      getWallet();
      dispatch(setFetchMyBet(true));
      showSuccessModal({
        title: t('bet.success.title'),
        content: t('bet.success.message'),
      });
    }
    if (error) {
      if (error.message === 'error.validate.balance.insufficient') {
        return handleInsufficientBalance();
      }
      if (error?.fieldErrors) {
        showErrorModal({
          content: et(error.fieldErrors.bets[0]),
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
      } else handleResponseError(error);
    }
  };

  const finalPrice = useMemo(() => {
    if (!currentUser || !currentUser.lowerPrice) {
      return {
        decimalPrice: bottomDecimalValue,
        americanPrice: bottomValue,
      };
    }
    const finalAmerican = lowerPrice(bottomValue);
    return {
      decimalPrice: convertOddsAmericanToDecimal(finalAmerican),
      americanPrice: finalAmerican,
    };
  }, [currentUser?.id, odd]);

  return (
    <ConditionButton
      isSkipCheckOnClick
      onClick={onClickBet}
      name="bet-button center-content-col"
      className={`bet-button bet-button${isActive ? '-active' : ''} ${showAnimation.border}`}
      onDoubleClick={onPlaceBet}
      testerCheck={true}
      content={
        isLoading ? (
          <div className="spinner"></div>
        ) : (
          <div className="bet-button__body">
            {!MONEY_LINE_MARKET.includes(market) && (
              <span
                className={`text-gray ${
                  platform.isPhone ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p3
                } `}
              >
                {`${getPoint()}`}
              </span>
            )}
            <span
              className={`botom-value${isActive ? '-active' : ''} ${showAnimation.text} ${
                platform.isPhone ? Typo.p5 : isTablet(platform) ? Typo.p5 : Typo.p3
              } `}
            >
              {converter === 'american' ? (
                <>{`${finalPrice.americanPrice > 0 ? '+' : ''}${new BigNumber(
                  finalPrice.americanPrice,
                ).toFixed(0)}`}</>
              ) : (
                <>{finalPrice.decimalPrice}</>
              )}
            </span>
          </div>
        )
      }
    ></ConditionButton>
  );
};

export default BetButton;
